import { useEffect, useRef, useState } from 'react';

import { isApiClientError, isSessionExpiredError } from './client.ts';
import { SESSION_EXPIRED_EVENT } from './session-expired.ts';

/**
 * Run `onExpired` when a signed-in call comes back 401 — for a console's root,
 * to swap itself back to the sign-in form. See session-expired.ts.
 */
export function useSessionExpired(onExpired: () => void): void {
  const latest = useRef(onExpired);
  latest.current = onExpired;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = () => latest.current();
    window.addEventListener(SESSION_EXPIRED_EVENT, listener);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, listener);
  }, []);
}

/**
 * Is there already a signed-in session, from a cookie a previous visit set?
 *
 * §3.7's session now survives a reload, so a fresh page load is no longer
 * "assume signed out" the way it was when the token lived only in React
 * state. `api` must be constructed with `token: () => undefined` — this
 * exists to answer whether the *cookie* alone is enough, not to lean on a
 * token this render has no way to already know.
 *
 * A network failure is not "no session". AbortError from React StrictMode
 * remounts must not be reported as an outage.
 */
export type ExistingSession =
  | { state: 'checking' }
  | { state: 'none' }
  | { state: 'unreachable'; error: unknown }
  | { state: 'found' };

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'AbortError'
  );
}

function isUnauthenticated(error: unknown): boolean {
  return (
    isSessionExpiredError(error) ||
    (isApiClientError(error) && error.status === 401)
  );
}

export function useExistingSession(api: {
  session(): Promise<{ authenticated: true }>;
}): ExistingSession {
  const [result, setResult] = useState<ExistingSession>({ state: 'checking' });

  useEffect(() => {
    let live = true;

    const onError = (error: unknown) => {
      if (!live || isAbortError(error)) return;
      if (isUnauthenticated(error)) {
        setResult({ state: 'none' });
        return;
      }
      setResult({ state: 'unreachable', error });
    };

    api.session().then(
      () => { if (live) setResult({ state: 'found' }); },
      (error: unknown) => {
        if (!live || isAbortError(error)) return;
        if (isUnauthenticated(error)) {
          setResult({ state: 'none' });
          return;
        }
        // One retry — the Vite proxy occasionally drops the first probe.
        api.session().then(
          () => { if (live) setResult({ state: 'found' }); },
          onError,
        );
      },
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return result;
}

/**
 * Load something, and keep the three outcomes distinct.
 *
 * "Loading", "failed" and "loaded but empty" look identical the moment a hook
 * collapses them into `data | null`, and somebody shown an empty table while
 * the request is still in flight concludes there is nothing there. On an
 * affiliate's commission screen that conclusion is "I earned nothing".
 */
export type Load<T> =
  | { state: 'loading' }
  | { state: 'error'; error: unknown }
  | { state: 'ready'; value: T };

export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): Load<T> {
  const [result, setResult] = useState<Load<T>>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    setResult({ state: 'loading' });
    load().then(
      (value) => { if (live) setResult({ state: 'ready', value }); },
      (error: unknown) => { if (live) setResult({ state: 'error', error }); },
    );
    // A response arriving after the reader has moved to another screen must not
    // overwrite what they are now looking at.
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return result;
}
